import {
  downscaleTarget,
  isReencodable,
  MAX_UPLOAD_DIMENSION,
  UPLOAD_IMAGE_QUALITY,
} from '@trilirivos/media-keys';

/**
 * TRIL-635: shrink an oversized photo before it is uploaded. Web half of the same change that
 * added `downscaleForUpload` on mobile — both consume the shared `downscaleTarget`, because the
 * recurring failure in this arc has been fixing one surface of a pair.
 *
 * FAILS OPEN, deliberately. If the image cannot be decoded or the canvas produces nothing, the
 * ORIGINAL File is returned and uploaded unchanged. Refusing would turn a size optimisation into
 * a blocked upload; the caller's existing type/size validation still bounds what can land.
 */
export async function downscaleImageFile(file: File): Promise<File> {
  // Animated formats and non-image types are passed through untouched — see isReencodable. The
  // rule is shared with mobile because keeping it local is precisely how mobile ended up without
  // a GIF bypass while web had one.
  if (!isReencodable(file.type)) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const target = downscaleTarget(
      bitmap.width,
      bitmap.height,
      MAX_UPLOAD_DIMENSION,
    );
    if (!target) {
      bitmap.close();
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, target.width, target.height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', UPLOAD_IMAGE_QUALITY);
    });
    if (!blob) return file;

    // The name carries the NEW extension: the server keys the S3 object off the mime type, and a
    // ".png" name on JPEG bytes is a lie that outlives this function.
    const name = file.name.replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}
